// Replacement-effect query. Pure. No React, no Three.js.
//
// ── Codex (Replacement Effect) ──
//
// "Replacement effects replace one event with another. These types of
// effects often use the word 'instead' and 'would,' with phrases like
// 'would die,' 'would take damage,' 'would successfully attack,' or
// 'would untap.' [...] Replacement effects do NOT use the storyline;
// they immediately resolve as soon as they are triggered."
//
// ── Architecture ──
//
// Each replacement effect lives as an automation entry with a
// would-trigger (WOULD_DIE, WOULD_TAKE_DAMAGE, etc.) on a card in
// play. The runtime asks this module for matching effects RIGHT
// BEFORE the underlying event commits:
//
//   const replacements = findDeathReplacements(dyingCard, meshes, getFullCard);
//   if (replacements.length > 0) { /* apply, skip the death */ }
//   else { sendCardToPile(dyingCard, 'Cemetery'); }
//
// Replacement entries declare WHAT they watch via `scope.where`. The
// supported scopes for WOULD_DIE today:
//   • 'self'. The watcher card watches its OWN death
//   • 'self_bearer'. Equipped artifact watches its bearer's death
//                      (Gilded Aegis pattern). Resolved via the
//                      equipped-card relationship on the host.
//
// Future scope extensions (handled by caller, not this module):
//   • 'nearby' / 'adjacent' / 'global'. Watcher reacts to OTHER
//     cards dying (Lady Iseult: "If another nearby allied minion
//     would die, return it to hand."). These need scope-resolver
//     plumbing for matching against the dying card; deferred to a
//     v1.78+ pass when we wire the broader scoped triggers.
//
// ── No storyline use ──
//
// Per codex, replacement effects don't go on the storyline. The
// runtime applies them synchronously inline with the event being
// replaced. No LIFO insert, no priority ordering across triggers.
// If multiple replacement effects compete for the same event, the
// controller of the affected card picks one (caller decides; this
// module returns all matches in declaration order).

import { TRIGGER } from './schema';
import { matchesFilter as cardFilterMatches } from './cardFilter';

/**
 * Find every active WOULD_DIE replacement effect that applies to
 * `dyingCard`. Returns an array of { watcher, fullWatcher, entry }
 * objects in declaration order. Caller picks one (or applies all,
 * depending on UX / codex resolution).
 *
 * @param dyingCard the card that's about to be sent to cemetery
 * @param meshes live Three.js mesh map
 * @param getFullCard (cardId) → catalog row
 */
export function findDeathReplacements(dyingCard, meshes, getFullCard, opts = {}) {
  return findEventReplacements(TRIGGER.WOULD_DIE, dyingCard, meshes, getFullCard, opts);
}

export function findSummonReplacements(card, meshes, getFullCard, opts = {}) {
  return findEventReplacements(TRIGGER.WOULD_BE_SUMMONED, card, meshes, getFullCard, opts);
}

function findEventReplacements(trigger, dyingCard, meshes, getFullCard, opts) {
  if (!dyingCard) return [];
  const matches = [];
  for (const [, mesh] of meshes) {
    const watcher = mesh?.userData?.cardInstance;
    if (!watcher) continue;
    const fullWatcher = getFullCard(watcher.cardId);
    const entries = fullWatcher?.automation || [];
    for (const entry of entries) {
      if (entry.trigger !== trigger) continue;
      if (matchesScope(entry, watcher, dyingCard, meshes, opts)) {
        matches.push({ watcher, fullWatcher, entry });
      }
    }
  }
  return matches;
}

/**
 * Find every active WOULD_DIE replacement controlled by `dyingPlayer`
 * whose scope is `player_self`. I.e. the player themselves are about
 * to die. Altar of Malachai pattern. Returns the same shape as
 * findDeathReplacements; caller picks one and applies its effects
 * (typically a SACRIFICE_HERE + PREVENT_DEATH semantic where preventing
 * the player's death blow is the consequence of the entry firing).
 *
 * @param dyingPlayer 'p1' | 'p2'. The player about to take a death blow
 * @param meshes live mesh map
 * @param getFullCard (cardId) → catalog row
 */
export function findPlayerDeathReplacements(dyingPlayer, meshes, getFullCard) {
  if (dyingPlayer !== 'p1' && dyingPlayer !== 'p2') return [];
  const wantedRotated = dyingPlayer === 'p2';
  const matches = [];
  for (const [, mesh] of meshes) {
    const watcher = mesh?.userData?.cardInstance;
    if (!watcher) continue;
    if (!!watcher.rotated !== wantedRotated) continue;
    const fullWatcher = getFullCard(watcher.cardId);
    const entries = fullWatcher?.automation || [];
    for (const entry of entries) {
      if (entry.trigger !== TRIGGER.WOULD_DIE) continue;
      if (entry.scope?.where !== 'player_self') continue;
      matches.push({ watcher, fullWatcher, entry });
    }
  }
  return matches;
}

/**
 * Find every active WOULD_TAKE_DAMAGE replacement that applies to a
 * card about to take damage. Same shape as findDeathReplacements; the
 * caller iterates the matches and applies their effects before
 * mutating the target's HP.
 *
 * @param targetCard the card about to take damage
 * @param sourceCard the card dealing the damage (may be null for
 *                   non-attack damage like spells; replacement
 *                   conditions that reference 'attacker' should
 *                   tolerate null)
 * @param meshes live Three.js mesh map
 * @param getFullCard (cardId) → catalog row
 */
export function findDamageReplacements(targetCard, sourceCard, meshes, getFullCard, opts = {}) {
  if (!targetCard) return [];
  const matches = [];
  for (const [, mesh] of meshes) {
    const watcher = mesh?.userData?.cardInstance;
    if (!watcher) continue;
    const fullWatcher = getFullCard(watcher.cardId);
    // v1.77g. Scan BOTH catalog automation and runtime-installed
    // transient entries (spell-deployed auras like Enduring Faith).
    // Transient entries carry an optional `expiresAtTurn` field that
    // the runtime prunes at the appropriate turn boundary.
    const catalogEntries = fullWatcher?.automation || [];
    const transientEntries = watcher._transientAutomation || [];
    for (const entry of catalogEntries) {
      if (entry.trigger !== TRIGGER.WOULD_TAKE_DAMAGE) continue;
      if (matchesScope(entry, watcher, targetCard, meshes, opts)) {
        matches.push({ watcher, fullWatcher, entry });
      }
    }
    for (const entry of transientEntries) {
      if (entry.trigger !== TRIGGER.WOULD_TAKE_DAMAGE) continue;
      if (matchesScope(entry, watcher, targetCard, meshes, opts)) {
        matches.push({ watcher, fullWatcher, entry });
      }
    }
  }
  return matches;
}

/**
 * Decide whether a watcher's WOULD_DIE entry applies to the dying
 * card. Today only 'self' and 'self_bearer' scopes are wired; other
 * scopes (nearby, adjacent, global with allied/enemy filters) require
 * the full scope resolver and are deferred to a v1.78+ broadening
 * pass. Unrecognized scopes return false (silent skip), so adding new
 * scopes won't double-fire existing entries.
 */
function matchesScope(entry, watcher, target, meshes, opts) {
  const where = entry.scope?.where || 'self';
  const filter = entry.scope?.filter || {};

  if (where === 'self') {
    return watcher.id === target.id;
  }
  if (where === 'self_bearer') {
    // Equipped artifact watches its bearer. Walk the meshes once to
    // find any card that has this watcher in its equippedCards array.
    for (const [, mesh] of meshes) {
      const candidate = mesh?.userData?.cardInstance;
      if (!candidate?.equippedCards) continue;
      const isBearer = candidate.equippedCards.some((e) => e.id === watcher.id);
      if (isBearer && candidate.id === target.id) return true;
    }
    return false;
  }

  // v1.77c. Broader scopes require a cell-locator. Caller passes
  // `opts.cellAt = (x, z) => ({ col, row })`. Without it we silently
  // skip. The watcher just doesn't fire (forward-compat with future
  // callers that don't pass cellAt).
  if (!opts?.cellAt) return false;
  const watcherMesh = findMeshById(meshes, watcher.id);
  const targetMesh  = findMeshById(meshes, target.id);
  if (!watcherMesh || !targetMesh) return false;
  const wCell = opts.cellAt(watcherMesh.position.x, watcherMesh.position.z);
  const tCell = opts.cellAt(targetMesh.position.x,  targetMesh.position.z);
  if (!wCell || !tCell) return false;

  // Filter that applies to the TARGET card. Supports the same shape
  // the scope resolver uses: type / subtype / status / allied /
  // exclude_self / or_filters.
  if (!matchesFilter(target, watcher, filter)) return false;

  if (where === 'here') {
    // Same cell as the watcher (Makeshift Barricade pattern).
    return wCell.col === tCell.col && wCell.row === tCell.row
      && (watcher.type === 'Site' || (watcher.subsurface || null) === (target.subsurface || null));
  }
  if (where === 'nearby') {
    // 8-neighbor + self (matches scope.js 'nearby' since v1.18a).
    const dx = Math.abs(wCell.col - tCell.col);
    const dy = Math.abs(wCell.row - tCell.row);
    return dx <= 1 && dy <= 1 && (watcher.subsurface || null) === (target.subsurface || null);
  }
  if (where === 'adjacent') {
    // 4-cardinal only.
    const dx = Math.abs(wCell.col - tCell.col);
    const dy = Math.abs(wCell.row - tCell.row);
    return (dx + dy) <= 1 && (watcher.subsurface || null) === (target.subsurface || null);
  }
  if (where === 'global') {
    return true; // any cell on the board
  }
  return false;
}

function findMeshById(meshes, id) {
  for (const [, mesh] of meshes) {
    if (mesh?.userData?.cardInstance?.id === id) return mesh;
  }
  return null;
}

// Replacement watchers use the canonical `matchesFilter` with the
// watcher card as the `source` so allied/enemy/exclude_self compare
// against it. The codex-default "exclude_self unless explicitly
// opted in" is preserved by defaulting filter.exclude_self to true
// when omitted (Lady Iseult's "another nearby allied minion" wording).
function matchesFilter(target, watcher, filter) {
  if (!filter || Object.keys(filter).length === 0) return true;
  const normalised = filter.exclude_self === false
    ? filter
    : { ...filter, exclude_self: true };
  return cardFilterMatches(target, normalised, { source: watcher });
}
